import {isNil} from "lodash"
import React, {PureComponent, createRef} from "react"
import PropTypes from "prop-types"
import {connect} from "react-redux"
import styled from "styled-components"
import moment from "moment"

import userService from "@irati/identity/services/users"
import {Card, Row, Col} from "@irati/ui/src/Layout"
import Loading from "@irati/ui/src/Loading"
import Success from "@irati/ui/src/Success"
import success from "@irati/ui/images/success.svg"
import GenericModal from "@irati/ui/src/GenericModal"
import {Radio, Select} from "@irati/ui/src/FormItem"
import Form from "@irati/ui/src/Form"
import {H1, Body} from "@irati/ui/src/Typography"
import Validation from "@irati/utils/validation"
import {genderOptions as genders} from "@irati/identity/constants/genders"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import DOBSelect from "@irati/ui/src/DOBSelect"
import {Firstname, Lastname, Email} from "@irati-web/fields/user"
import {BirthdayPreferences, TeamState, User, UserField, UserInput, UserManagedFields, UserPreferences} from "@irati/types"
import {dobObjectToString, dobStringToObject} from "@irati/identity/utils/user/dob"
import Flex from "@irati/ui/src/Flex"
import ValidationButton from "@irati/ui/src/FormItem/ValidationButton"
import Button from "@irati/ui/src/Button"
import workosService from "@irati/workos/services/workos"
import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"

import authentication from "@irati/auth"
import EditProfilePic from "../../components/EditProfilePic"
import VerifyPhoneNumber from "../../components/VerifyPhoneNumber"

const SSO_MANAGER = "SSO"
const DSYNC_MANAGER = "DIRECTORY_SYNC"
const RadioWrapper = styled.div`
  max-width: 340px;
`
const StyledForm = styled(Form)`
  margin-bottom: 2rem;
  .form-group {
    margin-bottom: 0;
  }
`
interface Props extends InjectIntl, WithAuth {
  isMobile: boolean
}

interface State {
  load: Promise<any> | null
  submit?: Promise<any> | null
  showSuccessMessage: boolean
  hasEdited: boolean
  teams: string
  firstName?: User["firstName"]
  lastName?: User["lastName"]
  gender?: User["gender"]
  dateOfBirth?: User["dateOfBirth"]
  userPreferences?: User["userPreferences"]
  externalStartDate?: User["externalStartDate"]
  email?: User["email"]
  language?: User["language"]
  directorySyncId?: string
  ssoId?: string
  managedFields?: UserManagedFields
}

class UpdateUserContainer extends PureComponent<Props, State> {
  dobInput = createRef<DOBSelect>()

  constructor(props: Props) {
    super(props)
    const load = this.fetchUserAndTeams()

    this.state = {
      load,
      submit: null,
      teams: "",
      showSuccessMessage: false,
      hasEdited: false,
      firstName: undefined,
      lastName: undefined,
      gender: undefined,
      dateOfBirth: undefined,
      externalStartDate: undefined,
      email: undefined,
      language: undefined,
      managedFields: undefined,
      userPreferences: {
        birthdayPreferences: BirthdayPreferences.Celebrate
      }
    }
  }

  static propTypes = {
    getTeams: PropTypes.func.isRequired
  }

  fetchUserAndTeams = () => {
    return userService.get(authentication.user().uuid!, {withTeams: true, withPreferences: true}).then((user) => {
      return workosService.getUser(user.uuid).then(({directorySyncId, ssoId}) => {
        const teamNames = user.teams.results.filter(({teamState}) => teamState === TeamState.Generic).map(({teamName}) => teamName)
        return this.setState({
          directorySyncId,
          ssoId,
          teams: teamNames.join(","),
          ...this.buildUserState(user)
        })
      })
    })
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const {
      auth: {user}
    } = nextProps
    if (user && user !== this.props.auth.user) {
      this.setState(this.buildUserState(user))
    }
  }

  buildGenders = () => {
    const {formatMessage} = this.props
    return genders.map((gender) => {
      return {...gender, label: formatMessage({id: gender.label})}
    })
  }

  buildUserState = (user: User): Partial<State> => {
    return {
      firstName: user ? user.firstName : undefined,
      lastName: user ? user.lastName : undefined,
      gender: user ? user.gender : undefined,
      dateOfBirth: user ? user.dateOfBirth : undefined,
      externalStartDate: user && user.externalStartDate ? moment(user?.externalStartDate).format("YYYY-MM-DD") : undefined,
      email: user ? user.email : undefined,
      language: user ? user.language : undefined,
      phoneNumber: !user || isNil(user.phoneNumber) ? undefined : user.phoneNumber,
      managedFields: user ? user.managedFields : undefined,
      userPreferences: user ? user.userPreferences : undefined
    }
  }

  updateInput = (label: keyof User, value: any) => {
    this.setState({
      hasEdited: true,
      [label]: value
    })
  }

  updateUserPreferences = (label: keyof UserPreferences, value: any) => {
    this.setState({
      ...this.state,
      hasEdited: true,
      userPreferences: {
        ...this.state.userPreferences,
        [label]: value
      }
    })
  }

  undoAll = () => {
    const {dateOfBirth} = this.props.auth.user
    if (this.dobInput.current) {
      this.dobInput.current.reset(dobObjectToString(dateOfBirth || {}))
    }
    this.fetchUserAndTeams()
    this.setState({
      hasEdited: false
    })
  }

  updateDetails = () => {
    const {
      auth: {user}
    } = this.props
    const {firstName, lastName, email, dateOfBirth, gender, language, userPreferences} = this.state

    const data: UserInput = {
      uuid: user.uuid,
      firstName: firstName && firstName.trim(),
      lastName: lastName && lastName.trim().length > 0 ? lastName.trim() : null,
      email,
      dateOfBirth,
      gender,
      language,
      userPreferences
    }

    const submit = userService.update({user: data, withPreferences: true}).then(() => {
      this.showSuccessMessage()
      this.setState({
        hasEdited: false
      })
      setTimeout(this.hideSuccessMessage, 2000)
    })

    this.setState({submit})

    return submit
  }

  showSuccessMessage = () => this.setState({showSuccessMessage: true})

  hideSuccessMessage = () => this.setState({showSuccessMessage: false})

  mobileValidation = () => {
    const {formatMessage} = this.props
    /* eslint-disable */
    return this.addSSOValidation(
      Validation.string()
        .transform((value) => (!value ? null : value))
        .min(7, formatMessage({id: "account.profile.fields.phone.validation.min"}))
        .max(15, formatMessage({id: "account.profile.fields.phone.validation.max"}))
        .matches(
          /^[+]?([\d]{0,3})?[\(\.\-\s]?(([\d]{1,3})[\)\.\-\s]*)?(([\d]{3,5})[\.\-\s]?([\d]{4})|([\d]{2}[\.\-\s]?){4})$/,
          formatMessage({id: "account.profile.fields.phone.validation.matches"})
        ),
      "phoneNumber"
    )
    /* eslint-enable */
  }

  addSSOValidation = (validation, key) => {
    const {
      formatMessage,
      auth: {user}
    } = this.props
    if (!user.isManagedExternally) {
      return validation
    }
    return validation.test("isSSOuser", formatMessage({id: "account.profile.addSSOValidation.test"}), (value) => {
      return (value || "") === (user[key] || "")
    })
  }

  buildEmailValidation = () => {
    const {formatMessage} = this.props
    return this.addSSOValidation(
      Validation.string(formatMessage({id: "account.profile.fields.email.validation.required"}))
        .max(255)
        .email(formatMessage({id: "account.profile.fields.email.validation.email"})),
      "email"
    )
  }

  handleManagedFieldsText = (field: UserField) => {
    const {managedFields} = this.state
    const {formatMessage} = this.props
    const fieldManager = managedFields?.fields?.find((f) => f.field === field)?.manager

    switch (fieldManager) {
      case SSO_MANAGER:
        return formatMessage({id: "account.profile.managedField"}, {manager: formatMessage({id: "account.profile.ssoManager"})})
      case DSYNC_MANAGER:
        return formatMessage({id: "account.profile.managedField"}, {manager: formatMessage({id: "account.profile.dsyncManager"})})
      default:
        return ""
    }
  }
  getBDayOptions() {
    const {formatMessage} = this.props
    return [
      {
        value: BirthdayPreferences.Celebrate,
        label: formatMessage({id: "account.profile.fields.bdayOption.celebrate"})
      },
      {
        value: BirthdayPreferences.Private,
        label: formatMessage({id: "account.profile.fields.bdayOption.private"})
      },
      {
        value: BirthdayPreferences.DoNotCelebrate,
        label: formatMessage({id: "account.profile.fields.bdayOption.donotcelebrate"})
      }
    ]
  }
  render() {
    const {
      load,
      submit,
      showSuccessMessage,
      teams,
      firstName,
      lastName,
      email,
      dateOfBirth,
      externalStartDate,
      gender,
      hasEdited,
      directorySyncId,
      ssoId,
      managedFields,
      userPreferences
    } = this.state
    const {formatMessage, isMobile, auth} = this.props
    const isManagedExternally = !!directorySyncId || !!ssoId

    return (
      <>
        <Card tiny noBorder>
          <div className="updateUser_form">
            <Form>
              <Row>
                <Col md={12}>
                  <H1 marginBottom={4}>
                    <FormattedMessage id="account.profile.header" />
                  </H1>
                </Col>
              </Row>
              <Loading load={load}>
                <Flex direction="column" maxWidth="480px">
                  <EditProfilePic />
                  <Body small marginBottom={1}>
                    <FormattedMessage id="account.profile.fields.teams.label" />
                  </Body>
                  <Body small marginBottom={1}>
                    {teams}
                  </Body>
                  <Firstname
                    value={firstName}
                    disabled={!!managedFields?.fields?.find((f) => f.field === UserField.FirstName)}
                    hint={isManagedExternally ? this.handleManagedFieldsText(UserField.FirstName) : null}
                    onChange={(value) => this.updateInput("firstName", value)}
                    validation={
                      managedFields?.fields?.find((f) => f.field === UserField.FirstName)
                        ? Validation.string("platform.defaultValidation.required")
                        : undefined
                    }
                  />
                  <Lastname
                    value={lastName}
                    disabled={!!managedFields?.fields?.find((f) => f.field === UserField.LastName)}
                    hint={isManagedExternally ? this.handleManagedFieldsText(UserField.LastName) : null}
                    onChange={(value) => this.updateInput("lastName", value)}
                    validation={
                      managedFields?.fields?.find((f) => f.field === UserField.LastName)
                        ? Validation.string("platform.defaultValidation.required")
                        : undefined
                    }
                  />
                  <Email
                    value={email}
                    disabled={isManagedExternally}
                    hint={isManagedExternally ? formatMessage({id: "account.profile.nonEditable"}) : null}
                    onChange={(value) => this.updateInput("email", value)}
                    validation={this.buildEmailValidation()}
                  />
                  <RadioWrapper>
                    <Radio
                      name="preferredPronoun"
                      label={formatMessage({id: "account.profile.fields.pronoun.label"})}
                      inline={!isMobile}
                      value={gender}
                      options={this.buildGenders()}
                      onChange={(value) => this.updateInput("gender", value)}
                    />
                  </RadioWrapper>
                  {externalStartDate && (
                    <DOBSelect
                      disabled
                      label={formatMessage({id: "account.profile.fields.employmentStartDate.label"})}
                      value={externalStartDate}
                      hint={formatMessage({id: "account.profile.fields.employmentStartDate.hint"})}
                    />
                  )}
                  <StyledForm>
                    <DOBSelect
                      label={formatMessage({id: "account.profile.fields.dob.label"})}
                      value={dobObjectToString(dateOfBirth || {})}
                      ref={this.dobInput}
                      onChange={(value) => this.updateInput("dateOfBirth", dobStringToObject(value || ""))}
                    />
                    <Select
                      name="birthdayPreferences"
                      value={userPreferences?.birthdayPreferences}
                      label={formatMessage({id: "account.profile.fields.title.bdayPreferenceSelector"})}
                      options={this.getBDayOptions()}
                      isClearable={false}
                      onChange={(value: string) => this.updateUserPreferences("birthdayPreferences", value)}
                    />
                  </StyledForm>
                  {!auth.productPermissions?.admin && <VerifyPhoneNumber />}
                </Flex>
                <Loading load={submit} useLoader={false} />
                <Flex marginTop={1}>
                  <ValidationButton id="saveChangesUserButton" large primary onClick={this.updateDetails} load={submit} marginRight={2}>
                    <FormattedMessage id="account.popup.save" />
                  </ValidationButton>
                  {hasEdited && (
                    <Button id="undoAllUserButton" large onClick={this.undoAll}>
                      <FormattedMessage id="account.popup.undo" />
                    </Button>
                  )}
                </Flex>
              </Loading>
            </Form>
          </div>
        </Card>
        <GenericModal hideActions show={showSuccessMessage} onHide={this.hideSuccessMessage} image={success}>
          <Success hideSuccessImage message={formatMessage({id: "account.profile.updateSuccessful"})} />
        </GenericModal>
      </>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    isMobile: state.mq.lessThan.medium
  }
}

export default injectIntl(withAuth(connect(mapStateToProps)(UpdateUserContainer), {withRef: true}))
